/**
 * Guesty listings → properties. `apps/api/src/routes/guesty-listings.ts`.
 *
 * A reservation reaches the books only through a property that claims its Guesty listing, so
 * until each listing here is a property, Guesty's stays are counted and set aside. Each row is a
 * listing with Guesty's own address and size; the company and market are suggested from the
 * address and are the owner's to confirm or change. Creating the property is the confirmation.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api.js';

interface Listing {
  id: string;
  name: string;
  title: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  sleeps: number | null;
  property: { id: string; name: string } | null;
  dismissed: { reason: string | null; at: string } | null;
  suggestedEntityId: string | null;
  suggestedMarketId: string | null;
}

interface ListingsResponse {
  entities: Array<{ id: string; name: string }>;
  markets: Array<{ id: string; name: string; state: string }>;
  listings: Listing[];
}

export function GuestyListingsPage(): ReactNode {
  const queryClient = useQueryClient();
  const [entityChoice, setEntityChoice] = useState<Record<string, string>>({});
  const [marketChoice, setMarketChoice] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const listings = useQuery({
    queryKey: ['guesty-listings'],
    queryFn: () => api.get<ListingsResponse>('/api/guesty/listings'),
  });

  const create = useMutation({
    mutationFn: async (rows: Array<{ listing: Listing; entityId: string; marketId: string }>) => {
      const made: string[] = [];
      for (const row of rows) {
        await api.post(`/api/guesty/listings/${encodeURIComponent(row.listing.id)}/property`, {
          entityId: row.entityId,
          marketId: row.marketId,
        });
        made.push(row.listing.name);
      }
      return made;
    },
    onSuccess: (made) => {
      setError(null);
      setMessage(
        `Created ${made.length} propert${made.length === 1 ? 'y' : 'ies'}. The next sync attaches ` +
          'their reservations.',
      );
    },
    onError: (err: unknown) => {
      setMessage(null);
      setError(err instanceof ApiError ? err.message : String(err));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['guesty-listings'] });
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });

  const dismiss = useMutation({
    mutationFn: (args: { listing: Listing; restore: boolean }) =>
      args.restore
        ? api.delete(`/api/guesty/listings/${encodeURIComponent(args.listing.id)}/dismiss`)
        : api.post(`/api/guesty/listings/${encodeURIComponent(args.listing.id)}/dismiss`, {
            reason: 'Not owned',
          }),
    onSuccess: (_r, args) => {
      setError(null);
      setMessage(
        args.restore
          ? `"${args.listing.name}" is back on the list.`
          : `"${args.listing.name}" marked not mine. Its reservations stay off the books.`,
      );
    },
    onError: (err: unknown) => {
      setMessage(null);
      setError(err instanceof ApiError ? err.message : String(err));
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['guesty-listings'] }),
  });

  const data = listings.data;
  const open = useMemo(
    () => (data?.listings ?? []).filter((l) => !l.property && !l.dismissed),
    [data],
  );
  const chosen = (l: Listing) => ({
    entityId: entityChoice[l.id] ?? l.suggestedEntityId ?? '',
    marketId: marketChoice[l.id] ?? l.suggestedMarketId ?? '',
  });
  const ready = open.filter((l) => chosen(l).entityId && chosen(l).marketId);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Guesty listings</h1>
          <p className="subtitle">
            Each Guesty listing becomes a property here. Until it does, its reservations are set
            aside rather than guessed onto a property.
          </p>
        </div>
      </header>

      {listings.isLoading && <p className="muted">Loading…</p>}
      {listings.isError && (
        <p className="error">Could not load the listings: {listings.error.message}</p>
      )}
      {error && <p className="error">{error}</p>}
      {message && <div className="alert alert-ok">{message}</div>}

      {data && (
        <section className="card">
          <p>
            <strong>{open.length}</strong> of {data.listings.length} listings are not properties
            yet.{' '}
            {ready.length > 0 && (
              <button
                type="button"
                disabled={create.isPending}
                onClick={() =>
                  create.mutate(ready.map((listing) => ({ listing, ...chosen(listing) })))
                }
              >
                {create.isPending
                  ? 'Creating…'
                  : `Create ${ready.length} propert${ready.length === 1 ? 'y' : 'ies'}`}
              </button>
            )}
          </p>
          {data.listings.length === 0 ? (
            <p className="empty">
              No Guesty listings yet. They arrive with the first Guesty sync (Settings → Sync now).
            </p>
          ) : (
            <table className="table compact">
              <thead>
                <tr>
                  <th>Listing</th>
                  <th>Address</th>
                  <th className="num">Beds</th>
                  <th className="num">Sleeps</th>
                  <th>Company</th>
                  <th>Market</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.listings.map((l) => {
                  const c = chosen(l);
                  return (
                    <tr key={l.id}>
                      <td title={l.title ?? undefined}>{l.name}</td>
                      <td className="muted">
                        {[l.street, l.city, l.state].filter(Boolean).join(', ')}
                      </td>
                      <td className="num">{l.bedrooms ?? '—'}</td>
                      <td className="num">{l.sleeps ?? '—'}</td>
                      {l.property ? (
                        <td colSpan={3}>
                          <span className="chip chip-ok">Property: {l.property.name}</span>
                        </td>
                      ) : l.dismissed ? (
                        <td colSpan={3}>
                          <span className="chip">Not mine</span>{' '}
                          <button
                            type="button"
                            className="link-button"
                            disabled={dismiss.isPending}
                            onClick={() => dismiss.mutate({ listing: l, restore: true })}
                          >
                            Restore
                          </button>
                        </td>
                      ) : (
                        <>
                          <td>
                            <select
                              aria-label={`Company for ${l.name}`}
                              value={c.entityId}
                              onChange={(e) =>
                                setEntityChoice({ ...entityChoice, [l.id]: e.target.value })
                              }
                            >
                              <option value="">Choose…</option>
                              {data.entities.map((e) => (
                                <option key={e.id} value={e.id}>
                                  {e.name}
                                  {e.id === l.suggestedEntityId ? ' (suggested)' : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              aria-label={`Market for ${l.name}`}
                              value={c.marketId}
                              onChange={(e) =>
                                setMarketChoice({ ...marketChoice, [l.id]: e.target.value })
                              }
                            >
                              <option value="">Choose…</option>
                              {data.markets.map((m) => (
                                <option key={m.id} value={m.id}>
                                  {m.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <button
                              type="button"
                              disabled={create.isPending || !c.entityId || !c.marketId}
                              onClick={() => create.mutate([{ listing: l, ...c }])}
                            >
                              Create
                            </button>{' '}
                            <button
                              type="button"
                              className="link-button"
                              disabled={dismiss.isPending}
                              onClick={() => dismiss.mutate({ listing: l, restore: false })}
                            >
                              Not mine
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
